/**
 * **视口变换** —— 屏幕坐标 ↔ 画布坐标、平移、缩放、适配。
 *
 * ## 为什么自己写（这段原来是 `d3-zoom` + Vue Flow 的 `viewport`）
 *
 * 这是"连画布也自己写"的第一块。它**纯到不能再纯**：
 * 四个数（`x` / `y` / `zoom`）进，四个数出 —— 所以它也是最该先写、
 * 最先被测住的一块。
 *
 * ```text
 *   屏幕坐标（相对画布容器左上角）      画布坐标（文档里的 x/y）
 *        screen  =  flow * zoom + (x, y)
 *        flow    = (screen - (x, y)) / zoom
 * ```
 *
 * ⚠️ **锚点坐标系有两套，别混**（这是最容易写错的地方）：
 *
 *   · `zoomAtScreen(vp, factor, hostPoint)` —— 锚点是**屏幕 / 容器**坐标。
 *     滚轮缩放用它：鼠标指哪儿就缩哪儿。
 *   · `pinchAround(vp, factor, flowPoint)`（在 `minimapGesture.ts`）——
 *     锚点是**画布**坐标。双指手势与小地图用它（那边已经先换算过了）。
 *
 * 两个函数的数学是同一个（都是"锚点在变换前后指向同一个画布点"），
 * 区别只在**入参是哪个坐标系**。搞混的后果是"缩放时画面往一个方向漂"。
 *
 * ## 归属
 *
 * `graph/canvas/` 是**我们自己的画布实现**。它的 `.ts` 部分零依赖
 * （没有 vue、没有 DOM、没有引擎），所以：
 *
 *   · 它是 `./graph` 入口的一部分（`verify:dist` 在产物上验"没有 vue"）；
 *   · 组件层（`.vue`）在同一个目录里，但**不进** `./graph` 入口。
 */
import { MAX_ZOOM, MIN_ZOOM, pinchAround } from '../minimapGesture'
import type { Viewport } from '../minimapGesture'
import type { Point } from '../../types/geometry'
import type { Box, Rect } from '../frame'

/**
 * 出厂视口 —— 和原来 `<VueFlow :default-viewport="{ x: 30, y: 20, zoom: 1 }">`
 * 传的值一致（左上角留一点边距，节点不会贴着工具栏）。
 */
export const DEFAULT_VIEWPORT: Viewport = { x: 30, y: 20, zoom: 1 }

/** 画布容器的尺寸（CSS 像素；**不含**视口变换） */
export interface HostSize {
  width: number
  height: number
}

export interface ZoomLimits {
  minZoom?: number
  maxZoom?: number
}

function limitsOf(options: ZoomLimits = {}): { min: number; max: number } {
  return { min: options.minZoom ?? MIN_ZOOM, max: options.maxZoom ?? MAX_ZOOM }
}

/** 夹到缩放范围里（顺手挡住 NaN / 0 / 负数这类会毁掉整个画面的值） */
export function clampViewport(viewport: Viewport, options: ZoomLimits = {}): Viewport {
  const { min, max } = limitsOf(options)
  const zoom = Number.isFinite(viewport.zoom) && viewport.zoom > 0 ? viewport.zoom : 1
  const clamped = Math.min(max, Math.max(min, zoom))
  const x = Number.isFinite(viewport.x) ? viewport.x : 0
  const y = Number.isFinite(viewport.y) ? viewport.y : 0
  return clamped === viewport.zoom && x === viewport.x && y === viewport.y
    ? viewport
    : { x, y, zoom: clamped }
}

/** 画布坐标 → 屏幕坐标（相对画布容器左上角） */
export function flowToScreen(viewport: Viewport, point: Point): Point {
  return {
    x: point.x * viewport.zoom + viewport.x,
    y: point.y * viewport.zoom + viewport.y,
  }
}

/** 屏幕坐标（相对画布容器左上角）→ 画布坐标 */
export function screenToFlow(viewport: Viewport, point: Point): Point {
  return {
    x: (point.x - viewport.x) / viewport.zoom,
    y: (point.y - viewport.y) / viewport.zoom,
  }
}

/** 整体平移（中键拖动 / 双指拖动 / 小地图拖动都用它） */
export function panBy(viewport: Viewport, dx: number, dy: number): Viewport {
  if (dx === 0 && dy === 0) return viewport
  return { x: viewport.x + dx, y: viewport.y + dy, zoom: viewport.zoom }
}

/**
 * 围绕**屏幕上的一点**缩放（滚轮 / 双击）。
 *
 * 语义：那个点在缩放前后指向**同一个画布点**（"指哪儿缩哪儿"）。
 */
export function zoomAtScreen(
  viewport: Viewport,
  factor: number,
  hostPoint: Point,
  options: ZoomLimits = {},
): Viewport {
  const next = clampViewport({ ...viewport, zoom: viewport.zoom * factor }, options)
  if (next.zoom === viewport.zoom) return viewport
  const anchor = screenToFlow(viewport, hostPoint)
  return pinchAround(viewport, next.zoom / viewport.zoom, anchor)
}

/** 一组盒子的**并集边界**（不算框的标题栏与内边距 —— `boundsFor` 那个是给框用的） */
export function boundsOfBoxes(boxes: readonly Rect[]): Rect | null {
  if (boxes.length === 0) return null
  const left = Math.min(...boxes.map((b) => b.x))
  const top = Math.min(...boxes.map((b) => b.y))
  const right = Math.max(...boxes.map((b) => b.x + b.width))
  const bottom = Math.max(...boxes.map((b) => b.y + b.height))
  return { x: left, y: top, width: right - left, height: bottom - top }
}

/**
 * 把一块内容**适配**进画布容器（`fitView`）。
 *
 * ## ⚠️ `padding` 的语义：**加在内容上**，不是"容器各边留多少"
 *
 * ```text
 *   zoom = min(容器宽 / (内容宽 × (1 + padding)), 容器高 / (内容高 × (1 + padding)))
 * ```
 *
 * 也就是说 `padding: 0.5` = "给内容四周留出它自身 50% 的余量"，
 * 而不是"容器四周各空 50%"。这两种读法在 0.1~0.2 时差别不大，
 * 但**在 0.5 时一个天一个地**：
 *
 * ```text
 *   我的第一版（容器各边留 padding）：usable = 容器 × (1 - 0.5×2) = 0
 *                                   → 除出来是 0 → 被夹到 minZoom = 0.2
 *   Vue Flow 的语义（加在内容上）： zoom = 容器 / (内容 × 1.5) → 正常放大
 * ```
 *
 * 宿主的 `useAddNode.focusNode` 传的正是 `padding: 0.5`（"定位到新节点，
 * 最多放大到 1.1 倍"），于是**新建节点后画面缩到最小** ——
 * 真机反馈的那条 bug 就是这个语义差。所以这里照 **Vue Flow 的算法**来：
 * 宿主里的那些 padding 取值是按那套语义挑的，换实现时不该改它们的含义。
 *
 * ⚠️ 剩下的边界情况都有明确答案（而不是"算出来是 NaN 就完了"）：
 *   · 没有内容 → 回出厂视口；
 *   · 内容尺寸为 0（单点 / 还没量过）→ `zoom = 1` 居中（不能除以 0）；
 *   · 算出来的缩放超出范围 → 夹住（此时内容"放不下"，但仍然居中）。
 */
export function fitViewport(
  content: Rect | null,
  host: HostSize,
  options: ZoomLimits & { padding?: number; maxZoom?: number } = {},
): Viewport {
  if (!content || host.width <= 0 || host.height <= 0) return DEFAULT_VIEWPORT

  const padding = Math.max(0, options.padding ?? 0.1)
  const { min, max } = limitsOf(options)

  const raw =
    content.width > 0 && content.height > 0
      ? Math.min(
          host.width / (content.width * (1 + padding)),
          host.height / (content.height * (1 + padding)),
        )
      : 1
  const zoom = Math.min(max, Math.max(min, Number.isFinite(raw) && raw > 0 ? raw : 1))

  /* 内容中心对齐容器中心 */
  const cx = content.x + content.width / 2
  const cy = content.y + content.height / 2
  return {
    x: host.width / 2 - cx * zoom,
    y: host.height / 2 - cy * zoom,
    zoom,
  }
}

/** 把某个画布点放到容器中心（"定位到节点"用） */
export function centerOn(
  target: Point | Rect,
  host: HostSize,
  zoom: number,
  options: ZoomLimits = {},
): Viewport {
  const point: Point = 'width' in target
    ? { x: target.x + target.width / 2, y: target.y + target.height / 2 }
    : target
  const z = clampViewport({ x: 0, y: 0, zoom }, options).zoom
  return {
    x: host.width / 2 - point.x * z,
    y: host.height / 2 - point.y * z,
    zoom: z,
  }
}

/** 两个视口是否**足够接近**（判断"要不要发 viewport-change"用） */
export function viewportEquals(a: Viewport, b: Viewport, epsilon = 0.01): boolean {
  return (
    Math.abs(a.x - b.x) < epsilon &&
    Math.abs(a.y - b.y) < epsilon &&
    Math.abs(a.zoom - b.zoom) < epsilon
  )
}

/**
 * 节点的**画布盒**：文档里的位置 + 量出来的尺寸。
 *
 * ⚠️ 尺寸是 0 的节点（还没量过 / 隐藏）**照样算进去**，
 * 只是它贡献的是"一个点"——`boundsOfBoxes` 会把它算进并集。
 * 过滤掉它会让"刚加进来的节点"在 `fitView` 时被忽略，
 * 用户看到的是"定位了但没对准"。
 */
export function boxOf(node: { id: string; position: Point; dimensions?: { width?: number; height?: number } }): Box {
  return {
    id: node.id,
    x: node.position.x,
    y: node.position.y,
    width: node.dimensions?.width ?? 0,
    height: node.dimensions?.height ?? 0,
  }
}

/** 多个节点的并集边界（`fitView` 的入参） */
export function contentBoundsOf(
  nodes: readonly { id: string; position: Point; dimensions?: { width?: number; height?: number } }[],
): Rect | null {
  return boundsOfBoxes(nodes.map(boxOf))
}
